import { useRef, useState } from "react";
import { useToast } from "@/components/feature/Toast";
import {
  uploadVerificationDoc,
  deleteVerificationDoc,
  type VerificationDoc,
} from "@/lib/seller-application";

interface VerificationDocsProps {
  userId: string;
  applicationId: string | null;
  docs: VerificationDoc[];
  onDocsChange: (docs: VerificationDoc[]) => void;
}

interface DocCardProps {
  type: "front" | "back";
  label: string;
  hint: string;
  userId: string;
  applicationId: string | null;
  doc: VerificationDoc | null;
  onUploaded: (doc: VerificationDoc) => void;
  onRemoved: () => void;
}

function DocCard({
  type,
  label,
  hint,
  userId,
  applicationId,
  doc,
  onUploaded,
  onRemoved,
}: DocCardProps) {
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function handleSelect(f: File | undefined) {
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  }

  async function handleUpload() {
    if (!file) return;
    setBusy(true);
    try {
      const uploaded = await uploadVerificationDoc(
        userId,
        applicationId,
        type,
        file
      );
      onUploaded(uploaded);
      showToast(`${label} subida correctamente.`, "success");
      setFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "No se pudo subir el documento.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!doc) return;
    setBusy(true);
    try {
      await deleteVerificationDoc(doc);
      onRemoved();
      showToast(`${label} eliminada.`, "success");
    } catch {
      showToast("No se pudo eliminar el documento.", "error");
    } finally {
      setBusy(false);
    }
  }

  const displayUrl = previewUrl;

  return (
    <div className="rounded-md border border-background-200 bg-background-50 p-4">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          handleSelect(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      <div className="flex items-start gap-4">
        <div className="h-20 w-16 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={label}
              className="h-full w-full object-cover object-top"
            />
          ) : doc ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-primary-600">
              <i className="ri-checkbox-circle-fill text-xl"></i>
              <span className="text-[10px] font-semibold">Subida</span>
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-foreground-300">
              <i className="ri-id-card-line text-2xl"></i>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="font-heading text-sm font-bold text-foreground-950">
            {label}
          </h4>
          <p className="mt-0.5 text-xs text-foreground-500">{hint}</p>

          {previewUrl ? (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleUpload}
                disabled={busy}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {busy && <i className="ri-loader-4-line animate-spin"></i>}
                Subir
              </button>
              <button
                type="button"
                onClick={() => {
                  if (previewUrl) URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                  setFile(null);
                }}
                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                Cancelar
              </button>
            </div>
          ) : doc ? (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-refresh-line"></i>
                Cambiar
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={busy}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-100 disabled:opacity-60"
              >
                <i className="ri-delete-bin-line"></i>
                Eliminar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mt-3 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-upload-line"></i>
              Subir {label.toLowerCase()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VerificationDocs({
  userId,
  applicationId,
  docs,
  onDocsChange,
}: VerificationDocsProps) {
  function upsertDoc(doc: VerificationDoc) {
    const others = docs.filter((d) => d.document_type !== doc.document_type);
    onDocsChange([...others, doc]);
  }

  function removeDoc(type: "front" | "back") {
    onDocsChange(docs.filter((d) => d.document_type !== type));
  }

  return (
    <div className="space-y-4">
      <DocCard
        type="front"
        label="Cédula frontal"
        hint="Foto clara del frente de tu cédula de identidad."
        userId={userId}
        applicationId={applicationId}
        doc={docs.find((d) => d.document_type === "front") ?? null}
        onUploaded={upsertDoc}
        onRemoved={() => removeDoc("front")}
      />
      <DocCard
        type="back"
        label="Cédula posterior"
        hint="Foto clara del reverso de tu cédula de identidad."
        userId={userId}
        applicationId={applicationId}
        doc={docs.find((d) => d.document_type === "back") ?? null}
        onUploaded={upsertDoc}
        onRemoved={() => removeDoc("back")}
      />

      <div className="flex items-start gap-3 rounded-md border border-accent-200 bg-accent-100/50 p-4">
        <i className="ri-shield-check-line text-accent-700"></i>
        <p className="text-sm text-accent-900">
          Tus documentos se guardan en un almacenamiento <strong>privado</strong>.
          Solo tu y el personal autorizado de VendeYa pueden verlos. Nunca se
          muestran en tu perfil público.
        </p>
      </div>
    </div>
  );
}